<template>
	<div class="lcsj-stat" :style="accentStyle">
		<div class="lcsj-stat__key">
			<span>{{ label }}</span>
			<el-tooltip v-if="tip" :content="tip" placement="top">
				<i class="el-icon-question lcsj-stat__tip"></i>
			</el-tooltip>
		</div>
		<div class="lcsj-stat__value" :class="{ 'is-negative': negative }">
			<template v-if="prefix">{{ prefix }}</template>{{ formattedValue }}<small v-if="unit">{{ unit }}</small>
		</div>
		<div v-if="desc" class="lcsj-stat__desc">
			<template v-if="trend !== ''">
				<span :class="trendClass">{{ trendIcon }} {{ trend }}</span>
			</template>
			<span>{{ desc }}</span>
		</div>
	</div>
</template>

<script>
/**
 * KPI 统计卡片（设计系统壳组件）
 * 视觉规范见 assets/scss/theme.scss
 *
 * 用法：
 *   <lcsj-stat-card label="会员总数" :value="1286" unit="人" desc="本月新增 32" trend="▲ 32" trend-up />
 */
export default {
	name: "lcsj-stat-card",
	props: {
		label: { type: String, default: "" },
		value: { type: [Number, String], default: "" },
		unit: { type: String, default: "" },
		prefix: { type: String, default: "" },
		desc: { type: String, default: "" },
		tip: { type: String, default: "" },
		/** 趋势值，如 "▲ 12.4%" */
		trend: { type: String, default: "" },
		/** 趋势方向：up 上升 / down 下降 / 空为中性 */
		trendUp: { type: Boolean, default: false },
		trendDown: { type: Boolean, default: false },
		/** 数值格式化：小数位，默认整数；传 null 表示不做千分位与小数处理 */
		decimals: { type: Number, default: null },
		/** 千分位分隔符 */
		thousands: { type: Boolean, default: true },
		/** 左侧色条：primary / success / warning / danger / 空=不显示 */
		accent: { type: String, default: "primary" }
	},
	methods: {
		/** 给数字字符串加千分位分隔符；thousands=false 或非数字时原样返回 */
		withThousands(str) {
			if (!this.thousands) return str;
			const s = String(str);
			// 已有分隔符或是纯整数以外形态，直接返回，避免重复处理
			if (!/^-?\d+(\.\d+)?$/.test(s)) return s;
			const neg = s.charAt(0) === "-";
			const body = neg ? s.slice(1) : s;
			const parts = body.split(".");
			parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
			return (neg ? "-" : "") + parts.join(".");
		}
	},
	computed: {
		formattedValue() {
			const v = this.value;
			if (v === "" || v === null || v === undefined) return "-";
			if (typeof v !== "number") return v;
			// 负数：取绝对值并强制保留 decimals 位小数，避免出现 "-1,234.5" 这类位数不齐的显示
			if (this.negative) {
				return this.withThousands(Math.abs(v).toFixed(this.decimals === null ? 2 : this.decimals));
			}
			// 指定小数位：先toFixed 再加千分位（此前只 toFixed，导致大额金额无分隔符）
			if (this.decimals !== null) {
				return this.withThousands(v.toFixed(this.decimals));
			}
			return this.withThousands(String(v));
		},
		negative() {
			return typeof this.value === "number" && this.value < 0;
		},
		trendClass() {
			if (this.trendUp) return "lcsj-stat__trend--up";
			if (this.trendDown) return "lcsj-stat__trend--down";
			return "";
		},
		trendIcon() {
			if (this.trendUp) return "▲";
			if (this.trendDown) return "▼";
			return "";
		},
		accentStyle() {
			const map = {
				primary: "var(--app-primary)",
				success: "var(--app-success)",
				warning: "var(--app-warning)",
				danger: "var(--app-danger)"
			};
			const c = map[this.accent];
			return c ? { borderLeftColor: c } : {};
		}
	}
};
</script>

<style scoped>
.lcsj-stat {
	position: relative;
	background: var(--app-surface);
	border: 1px solid var(--app-border);
	border-left: 3px solid var(--app-primary);
	border-radius: var(--app-radius-md);
	padding: 12px 14px;
	box-shadow: var(--app-shadow-sm);
	overflow: hidden;
	transition: box-shadow 0.2s;
}

.lcsj-stat:hover {
	box-shadow: var(--app-shadow-md);
}

.lcsj-stat__key {
	font-size: var(--app-font-base);
	color: var(--app-text-secondary);
	display: flex;
	align-items: center;
	gap: 4px;
}

.lcsj-stat__tip {
	color: var(--app-text-muted);
	cursor: help;
	font-size: var(--app-font-sm);
}

.lcsj-stat__value {
	font-size: var(--app-font-stat);
	font-weight: 700;
	margin-top: 5px;
	letter-spacing: -0.4px;
	font-variant-numeric: tabular-nums;
	color: var(--app-text);
	line-height: 1.25;
}

.lcsj-stat__value.is-negative {
	color: var(--app-danger-text);
}

.lcsj-stat__value small {
	font-size: var(--app-font-base);
	font-weight: 500;
	color: var(--app-text-muted);
	margin-left: 3px;
}

.lcsj-stat__desc {
	font-size: var(--app-font-sm);
	color: var(--app-text-muted);
	margin-top: 3px;
	display: flex;
	align-items: center;
	gap: 5px;
	flex-wrap: wrap;
}

.lcsj-stat__trend--up {
	color: var(--app-success-text);
}

.lcsj-stat__trend--down {
	color: var(--app-danger-text);
}
</style>